import React, { useState, useCallback, useRef, useEffect, ChangeEvent } from 'react';
import UserReviewsSegment, { UserReviewType } from './UserReviewsSegment';
import { ReviewResponse } from '../../../../../types/api/review';
import { ReviewsCounts } from '../../../../../hooks/useUserReviewsQuery';
import { useUserReviewsQuery } from '../../../../../hooks/useUserReviewsQuery';
import ModalSheet from '../../../../basic/ModalSheet';
import { UserReviewCard } from './UserReviewCard';
import { pageSize } from '../../../../../services/settings';
import { Placeholder, createErrorPlaceholder } from '../../../../basic';
import { Stack, SkeletonBox } from '@primer/react'
import { UnverifiedIcon } from '@primer/octicons-react';

interface UserReviewModalProps {
    userId: string;
    counter: ReviewsCounts
    isOpen: boolean;
    onClose: () => void;
}

export const UserReviewModal: React.FC<UserReviewModalProps> = ({ isOpen, onClose, counter, userId }) => {
    const [openedTab, setOpenedTab] = useState<UserReviewType>(UserReviewType.fromBuyers);
    const prevIsOpenRef = useRef<boolean>(false);
    const reviewsListRef = useRef<HTMLDivElement | null>(null);


    const fromSellersQuery = useUserReviewsQuery(UserReviewType.fromSellers, 10, userId);
    const fromBuyersQuery = useUserReviewsQuery(UserReviewType.fromBuyers, 10, userId);

    const {
        isLoading: isLoadingFromSellers,
        data: fromSellersData,
        fetchNextPage: fetchNextPageFromSellers,
        hasNextPage: hasNextPageFromSellers,
        isFetchingNextPage: isFetchingNextPageFromSellers,
        refetch: refetchFromSellers,
    } = fromSellersQuery;

    const {
        isLoading: isLoadingFromBuyers,
        data: fromBuyersData,
        fetchNextPage: fetchNextPageFromBuyers,
        hasNextPage: hasNextPageFromBuyers,
        isFetchingNextPage: isFetchingNextPageFromBuyers,
        refetch: refetchFromBuyers,
    } = fromBuyersQuery;

    useEffect(() => {
        refetchList()
    }, [openedTab]);

    useEffect(() => {
        if (isOpen && !prevIsOpenRef.current) {
            setOpenedTab(UserReviewType.fromBuyers);
            refetchList()
        }

        prevIsOpenRef.current = isOpen;
    }, [isOpen]);

    const refetchList = () => {
        switch (openedTab) {
            case (UserReviewType.fromBuyers):
                refetchFromBuyers()
                break
            case UserReviewType.fromSellers:
                refetchFromSellers();
                break
        }
    }

    const handleScroll = () => {
        if (reviewsListRef.current) {
            const { scrollTop, scrollHeight, clientHeight } = reviewsListRef.current;

            const hasScroll = scrollHeight > clientHeight;
            const isNearBottom = scrollTop + clientHeight >= scrollHeight - 5;

            if (hasScroll && isNearBottom) {
                switch (openedTab) {
                    case (UserReviewType.fromBuyers):
                        if (hasNextPageFromBuyers && !isFetchingNextPageFromBuyers) {
                            fetchNextPageFromBuyers();
                        }
                        break
                    case UserReviewType.fromSellers:
                        if (hasNextPageFromSellers && !isFetchingNextPageFromSellers) {
                            fetchNextPageFromSellers();
                        }
                        break
                }
            }
        }
    };

    const emptyListDescription = () => {
        switch (openedTab) {
            case UserReviewType.fromBuyers:
                return `Данному пользователю еще не успели
    оставить отзывы другие покупатели`
            case UserReviewType.fromSellers:
                return `Данному пользователю еще не успели
оставить отзывы другие продавцы`
        }
    }

    const PlaceholderView = () => {
        return (
            <Placeholder
                icon={<UnverifiedIcon size={24} className="opacity-40" />}
                header="Отзывов еще нет"
                description={emptyListDescription()}
                button={undefined}
                isModal={true}
            />
        )
    }

    return (
        <ModalSheet
            isOpen={isOpen}
            onClose={onClose}
            title="Отзывы"
            size="middle"
            headerSize="large"
        >
            {({ setTitle }) => (
                <Stack className='mt-[16px]'>
                    <UserReviewsSegment
                        counter={counter}
                        tabChanged={(tab) => setOpenedTab(tab)} />
                    {{
                        [UserReviewType.fromBuyers]: (
                            <div>
                                <div
                                    ref={reviewsListRef}
                                    onScroll={handleScroll}
                                    className="max-h-screen overflow-y-auto"
                                >
                                    {fromBuyersData?.pages.map((page, i) => (
                                        <React.Fragment key={i}>
                                            {page.list.map((e: ReviewResponse) => (
                                                <UserReviewCard key={e.id} review={e} />
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </div>
                                {!isLoadingFromBuyers &&
                                    !isFetchingNextPageFromBuyers &&
                                    (fromBuyersData?.pages?.flatMap((p) => p.list).length ?? 0) === 0 && (
                                        <PlaceholderView />
                                    )}
                                {(isFetchingNextPageFromBuyers || isLoadingFromBuyers) && <SkeletonBox height="4rem" />}
                            </div>
                        ),

                        [UserReviewType.fromSellers]: (
                            <div>
                                <div
                                    ref={reviewsListRef}
                                    onScroll={handleScroll}
                                    className="max-h-screen overflow-y-auto"
                                >
                                    {fromSellersData?.pages.map((page, i) => (
                                        <React.Fragment key={i}>
                                            {page.list.map((e: ReviewResponse) => (
                                                <UserReviewCard key={e.id} review={e} />
                                            ))}
                                        </React.Fragment>
                                    ))}
                                </div>
                                {!isLoadingFromSellers &&
                                    !isFetchingNextPageFromSellers &&
                                    (fromSellersData?.pages?.flatMap((p) => p.list).length ?? 0) === 0 && (
                                        <PlaceholderView />
                                    )}
                                {(isFetchingNextPageFromSellers || isLoadingFromSellers) && <SkeletonBox height="4rem" />}
                            </div>
                        ),
                    }[openedTab]}

                    {/* <div
                        ref={reviewsListRef}
                        onScroll={handleScroll}
                        className="flex flex-col md:grid md:grid-cols-2 gap-4 max-h-screen overflow-y-auto"
                    >
                        {data?.pages.map((page, i) => (
                            <React.Fragment key={i}>
                                {page.list.map((e: ReviewResponse, index) => (
                                    <UserReviewCard
                                        key={e.id}
                                        review={e}
                                    />
                                ))}
                            </React.Fragment>
                        ))}
                    </div> */}
                    {/*{isFetched && !isFetchingNextPage && (data?.pages?.flatMap(page => page.list).length ?? 0) === 0 && (
                        <Placeholder
                            icon={<UnverifiedIcon size={24} className='opacity-40' />}
                            header='Отзывов еще нет'
                            description={emptyListDescription()}
                            button={undefined}
                        />
                    )} */}
                    {/*Индикатор загрузки*/}

                    {/* {(isFetchingNextPage || isLoading) &&
                        <SkeletonBox height="4rem" />
                    } */}
                </Stack>
            )}
        </ModalSheet>
    );
};